/**
 * Cryptographic Utilities for Tradovate Authentication
 * Sources:
 * - Coach: /Users/victorvins/Projects/innovation-lab-coach/coach/src/tradovateapi/authenticator.ts:22-46,172-192
 * - RFC: docs/RFC-001-AUTH-BOOTSTRAP-ARCHITECTURE.md Example 5
 */

import jsSHA from 'jssha'
import type { AccessTokenRequest } from '../ops/accessTokenRequest'

// HMAC keys from Coach authenticator.ts
const NORMAL_HMAC_KEY = '035a1259-11e7-485a-aeae-9b6016579351'
const TRYIT_HMAC_KEY = 'snEi9wvjb8mi4R3bwmiErb'

// Fields used in HMAC signature (order matters!)
const SIGN_FIELDS = ['chl', 'deviceId', 'name', 'password', 'appId'] as const

/**
 * Reverse string helper
 * From Coach authenticator.ts:37
 */
function reverse(str: string): string {
    return str.split('').reverse().join('')
}

/**
 * Password obfuscation for normal login
 * EXACTLY matches Coach authenticator.ts:38-46
 *
 * Algorithm:
 * 1. Rotate password by (username.length % password.length)
 * 2. Reverse the rotated string
 * 3. Base64 encode
 *
 * @param name - Username
 * @param password - Plain password
 * @returns Base64-encoded obfuscated password
 */
export function obfuscatePassword(name: string, password: string): string {
    const chunkSize = name.length % password.length
    const rotated = password.slice(chunkSize) + password.slice(0, chunkSize)
    const reversed = reverse(rotated)
    return btoa(reversed)
}

/**
 * HMAC sign authentication request
 * EXACTLY matches Coach authenticator.ts:22-35
 *
 * IMPORTANT: Call BEFORE password obfuscation!
 *
 * Adds fields:
 * - cid: "1" (client ID)
 * - chl: timestamp offset from 1581000000000
 * - sec: HMAC-SHA256 signature (hex)
 *
 * @param req - Request object with plain password
 * @returns Modified request with signature fields
 */
export function signRequest(req: AccessTokenRequest): AccessTokenRequest {
    try {
        // Add challenge fields
        req.cid = '1'
        req.chl = `${Date.now() - 1581000000000}`

        // Build message from specific fields (order matters!)
        const msg = SIGN_FIELDS
            .map(field => (req[field as keyof AccessTokenRequest] as string) || '')
            .join('')

        // Create HMAC-SHA256 signature
        const shaObj = new jsSHA('SHA-256', 'TEXT')
        shaObj.setHMACKey(NORMAL_HMAC_KEY, 'TEXT')
        shaObj.update(msg)
        const signature = shaObj.getHMAC('HEX')

        req.sec = signature

        return req
    } catch (e) {
        console.error('HMAC signing failed:', e)
        return req
    }
}

/**
 * Generate Try-It mode credentials
 * EXACTLY matches Coach authenticator.ts:172-192
 *
 * Algorithm:
 * 1. Generate random UUID as username
 * 2. Create HMAC-SHA256 of "{username}:{deviceId}" with try-it key
 * 3. Convert HMAC to base64 as password
 *
 * NOTE: Uses DIFFERENT HMAC key than normal login!
 * NOTE: Password is already in correct format (NO obfuscation needed)
 *
 * @param deviceId - Device identifier
 * @returns Generated credentials for try-it mode
 */
export function generateTryItCredentials(deviceId: string): {
    username: string
    password: string
} {
    const username = generateGuid()

    const shaObj = new jsSHA('SHA-256', 'TEXT')
    shaObj.setHMACKey(TRYIT_HMAC_KEY, 'TEXT')
    shaObj.update(`${username}:${deviceId}`)
    const hmac = shaObj.getHMAC('ARRAYBUFFER')

    // Convert HMAC bytes to base64
    const password = btoa(String.fromCharCode(...new Uint8Array(hmac)))

    return { username, password }
}

/**
 * Generate UUID v4
 * Standard UUID generation
 */
export function generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}
